import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  useGetQuizByIdQuery,
  useUpdateQuizMutation,
} from "../../slices/quizApiSlice";

import { useGetSubjectsQuery } from "../../slices/subjectApiSlice";

const QuizEditScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [subject, setSubject] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const [durationMinutes, setDurationMinutes] = useState(30);

  const [passingPercentage, setPassingPercentage] = useState(40);

  const [allowMultipleAttempts, setAllowMultipleAttempts] = useState(false);

  const [maxAttempts, setMaxAttempts] = useState(1);

  const [status, setStatus] = useState("draft");

  const [isActive, setIsActive] = useState(true);

  const { data: quiz, isLoading, error } = useGetQuizByIdQuery(id);

  const { data: subjects = [] } = useGetSubjectsQuery();

  const [updateQuiz, { isLoading: loadingUpdate }] = useUpdateQuizMutation();

  useEffect(() => {
    if (quiz) {
      setSubject(
        quiz.questionBankSubject?._id ||
          quiz.questionBankSubject ||
          quiz.subject?._id ||
          quiz.subject ||
          "",
      );

      setName(quiz.name || "");

      setDescription(quiz.description || "");

      setDurationMinutes(quiz.durationMinutes ?? 30);

      setPassingPercentage(quiz.passingPercentage ?? 40);

      setAllowMultipleAttempts(quiz.allowMultipleAttempts ?? false);

      setMaxAttempts(quiz.maxAttempts ?? 1);

      setStatus(quiz.status || "draft");

      setIsActive(quiz.isActive ?? true);
    }
  }, [quiz]);

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!subject || !name.trim()) {
      toast.error("Subject and quiz name are required");

      return;
    }

    if (status === "published" && quiz?.status !== "published") {
      const confirmPublish = window.confirm(
        "Are you sure you want to publish this quiz?",
      );

      if (!confirmPublish) return;
    }

    try {
      await updateQuiz({
        quizId: id,

        questionBankSubject: subject,

        name: name.trim(),

        description: description.trim(),

        durationMinutes: Number(durationMinutes),

        passingPercentage: Number(passingPercentage),

        allowMultipleAttempts,

        maxAttempts: allowMultipleAttempts ? Number(maxAttempts) : 1,

        status,

        isActive,
      }).unwrap();

      toast.success("Quiz updated successfully");

      navigate("/quizzes");
    } catch (err) {
      toast.error(err?.data?.message || err?.error || "Failed to update quiz");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <Link
          to="/quizzes"
          className="inline-flex items-center gap-2 mb-6 rounded-xl bg-white px-4 py-2 shadow text-blue-600"
        >
          ← Back to Quizzes
        </Link>

        <div className="rounded-3xl bg-white/80 backdrop-blur-lg shadow-2xl p-6 md:p-10">
          <h1 className="text-4xl font-bold text-gray-800 text-center mb-2">
            Edit Quiz
          </h1>

          <p className="text-center text-gray-500 mb-8">
            Update quiz details. Blueprint and question generation are managed
            separately.
          </p>

          {loadingUpdate && (
            <div className="mb-6 rounded-xl bg-blue-100 p-4 text-blue-700 text-center">
              Updating quiz...
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-10">Loading quiz details...</div>
          ) : error ? (
            <div className="bg-red-100 text-red-700 p-4 rounded-xl">
              {error?.data?.message || error.error}
            </div>
          ) : (
            <form
              onSubmit={submitHandler}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              <div>
                <label className="block mb-2 font-semibold">Subject *</label>

                <select
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full rounded-xl border px-4 py-3"
                >
                  <option value="">Select Subject</option>

                  {subjects.map((item) => (
                    <option key={item._id} value={item._id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-2 font-semibold">Quiz Name *</label>

                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-xl border px-4 py-3"
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold">
                  Duration Minutes
                </label>

                <input
                  type="number"
                  min="1"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(e.target.value)}
                  className="w-full rounded-xl border px-4 py-3"
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold">
                  Passing Percentage
                </label>

                <input
                  type="number"
                  min="0"
                  max="100"
                  value={passingPercentage}
                  onChange={(e) => setPassingPercentage(e.target.value)}
                  className="w-full rounded-xl border px-4 py-3"
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold">Status</label>

                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full rounded-xl border px-4 py-3"
                >
                  <option value="draft">Draft</option>

                  <option value="published">Published</option>

                  <option value="archived">Archived</option>
                </select>
              </div>

              <div>
                <label className="block mb-2 font-semibold">
                  Maximum Attempts
                </label>

                <input
                  type="number"
                  min="1"
                  disabled={!allowMultipleAttempts}
                  value={maxAttempts}
                  onChange={(e) => setMaxAttempts(e.target.value)}
                  className="w-full rounded-xl border px-4 py-3 disabled:bg-gray-200"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block mb-2 font-semibold">Description</label>

                <textarea
                  rows="5"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full rounded-xl border px-4 py-3"
                />
              </div>

              <div className="md:col-span-2 grid md:grid-cols-2 gap-4">
                <label className="flex items-center gap-3 border rounded-xl p-4">
                  <input
                    type="checkbox"
                    checked={allowMultipleAttempts}
                    onChange={(e) => setAllowMultipleAttempts(e.target.checked)}
                  />
                  Allow Multiple Attempts
                </label>

                <label className="flex items-center gap-3 border rounded-xl p-4">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                  Active Quiz
                </label>
              </div>

              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={loadingUpdate}
                  className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-4 text-white font-semibold disabled:opacity-60"
                >
                  {loadingUpdate ? "Updating..." : "Update Quiz"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizEditScreen;
